import { fetchEventSource } from "@microsoft/fetch-event-source";
import localhost from "apis/localhost";
import {
  EVENT_TITLES,
  PHASE_BY_EVENT,
  SSE_MAX_RECONNECT_ATTEMPTS,
  SSE_RECONNECT_BACKOFF_MS,
  SSE_STALL_TIMEOUT_MS,
} from "constants/outlineGeneration";
import { PROPOSAL_OUTLINE_STAGES } from "constants/proposals";
import { modalTypes } from "constants/types";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import { getDocTypes, getDocuments } from "slices/documentUploads/actions";
import {
  confirmGate,
  reExtractRequirements,
  submitGateAnswers,
} from "slices/proposalOutline/actions/gateActions";
import {
  fetchProposalOutlineByOwner,
  resumeOutline,
  retryOutline,
} from "slices/proposalOutline/actions/outlineActions";
import {
  addRequirement as addRequirementAction,
  removeRequirement as removeRequirementAction,
  updateRequirement as updateRequirementAction,
} from "slices/proposalOutline/actions/requirementActions";
import {
  getOwnerUrl,
  uploadEntityTypeForOwner,
} from "slices/proposalOutline/actions/shared";
import { PROPOSAL_OUTLINE_OWNER_TYPES } from "slices/proposalOutline/constants";
import {
  addProgressHistory,
  applyClassificationDetected,
  applyDocumentProgress,
  applyOutlineProgress,
  clearClarification,
  clearClarificationAnswers,
  resetGenerationProgress,
  setClarification,
  setConnectionState,
  setCurrentPhase,
  setDocumentIds,
  setGateState,
  setGenerateFailed,
  setProcessingStatus,
  setProgressError,
  setProgressHistory,
  setStructureProposal,
} from "slices/proposalOutline/outlineSlice";
import { setHasProposalOutline } from "slices/proposals/proposal/proposalSlice";
import { getErrorMessage } from "utils";

const {
  PENDING,
  IN_PROGRESS,
  AWAITING_CLARIFICATION,
  CLASSIFYING,
  AWAITING_TYPE_REVIEW,
  EXTRACTING,
  AWAITING_REQ_REVIEW,
  GENERATING,
  COMPLETED,
  FAILED,
} = PROPOSAL_OUTLINE_STAGES;

const GATE_TYPE_REVIEW = "TYPE_REVIEW";
const GATE_REQ_REVIEW = "REQ_REVIEW";

const LOST_CONNECTION_MESSAGE =
  "Lost connection to the generation service. The generation may still be running — click Retry to reconnect.";

// Statuses for which a page reload should reconnect the SSE stream on mount:
// a live/pending run, a paused clarification/gate wait, or one of the staged
// in-progress phases of the requirement-driven pipeline (reachable right after
// confirmTypes()/confirmRequirements()/reExtract()). COMPLETED/FAILED are
// deliberately excluded — no live run to reconnect to.
const RECONNECTABLE_STATUSES = new Set([
  PENDING,
  IN_PROGRESS,
  AWAITING_CLARIFICATION,
  CLASSIFYING,
  AWAITING_TYPE_REVIEW,
  EXTRACTING,
  AWAITING_REQ_REVIEW,
  GENERATING,
]);

const buildStreamUrl = (outlineId) => {
  const flowId = `proposal-outline-${outlineId}`;
  return `/api/proposal-outlines/stream/connect?flowId=${flowId}`;
};

export const useProposalOutline = (ownerType, ownerId, checkBeforeAction) => {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);
  const entityType = uploadEntityTypeForOwner(ownerType);
  const sseRef = useRef({
    abortController: null,
    headers: {},
    seenIds: new Set(),
    stallTimer: null,
    reconnectAttempts: 0,
    // A reconnect waits out a backoff before opening the next stream. Aborting the current
    // controller does not cancel that wait, so without these the timer fires after unmount and
    // opens a stream nothing will ever abort — one that keeps writing another proposal's outline
    // into the shared slice.
    backoffTimer: null,
    unmounted: false,
    // Bumped by every run of the mount effect. `unmounted` alone cannot catch a superseded call:
    // on an owner change the cleanup sets it true and the re-run immediately sets it back to
    // false, so an await still in flight for the PREVIOUS owner resumes seeing a mounted hook.
    epoch: 0,
  });
  // Guards generate/regenerate/resume against double submits (e.g. double
  // click). Kept separate from sseRef: it tracks a single in-flight
  // outer-request, unrelated to the SSE connection/stall state above.
  const pendingRef = useRef(false);

  const clearStallTimer = () => {
    if (sseRef.current.stallTimer) {
      clearTimeout(sseRef.current.stallTimer);
      sseRef.current.stallTimer = null;
    }
  };

  const armStallTimer = () => {
    clearStallTimer();
    sseRef.current.stallTimer = setTimeout(onStall, SSE_STALL_TIMEOUT_MS);
  };

  const handleFailedEvent = (data) => {
    if (sseRef.current.abortController) {
      sseRef.current.abortController.abort();
    }
    dispatch(setGenerateFailed(data));
  };

  const handleCompletedEvent = () => {
    if (sseRef.current.abortController) {
      sseRef.current.abortController.abort();
    }
    dispatch(clearClarification());
    dispatch(hideModal(modalTypes.CLARIFICATION_REQUIRED));
    dispatch(setProgressHistory());
    if (ownerType === PROPOSAL_OUTLINE_OWNER_TYPES.PROPOSAL) {
      dispatch(setHasProposalOutline(true));
    }
    return dispatch(fetchProposalOutlineByOwner(ownerType, ownerId));
  };

  // True once this call's effect run has been superseded — the hook unmounted, or the owner
  // changed while an await was in flight. Anything that opens a stream or dispatches into the
  // shared outline slice must check it, or a call started for the previous owner lands on the
  // current one: connect() aborts the live owner's controller and streams the wrong run's events.
  const isStale = (epoch) =>
    sseRef.current.unmounted || epoch !== sseRef.current.epoch;

  const clearBackoffTimer = () => {
    if (sseRef.current.backoffTimer) {
      clearTimeout(sseRef.current.backoffTimer);
      sseRef.current.backoffTimer = null;
    }
  };

  const reconnectAfterBackoff = async (attempt) => {
    const delay =
      SSE_RECONNECT_BACKOFF_MS[attempt] ??
      SSE_RECONNECT_BACKOFF_MS[SSE_RECONNECT_BACKOFF_MS.length - 1];

    clearBackoffTimer();
    await new Promise((resolve) => {
      sseRef.current.backoffTimer = setTimeout(resolve, delay);
    });

    if (sseRef.current.unmounted) {
      return;
    }

    try {
      await reconnectInternal();
    } catch (error) {
      console.error("SSE reconnect attempt failed:", error);
      await runReconnectRoutine();
    }
  };

  // The attempt count lives on the ref, not in an argument: every caller used to pass 0, so the
  // cap only ever bounded a chain of thrown failures. A stream the server accepts and then closes
  // comes back through onStall, which reset the count each time and looped forever. Only real
  // traffic clears the budget now — see onMessage and the fresh-run branch of connect.
  const runReconnectRoutine = async () => {
    if (sseRef.current.unmounted) {
      return;
    }
    const attempt = sseRef.current.reconnectAttempts;
    if (attempt >= SSE_MAX_RECONNECT_ATTEMPTS) {
      dispatch(setConnectionState("lost"));
      dispatch(setGenerateFailed({ message: LOST_CONNECTION_MESSAGE }));
      return;
    }
    sseRef.current.reconnectAttempts = attempt + 1;
    await reconnectAfterBackoff(attempt);
  };

  const onStall = async () => {
    clearStallTimer();
    if (sseRef.current.abortController) {
      sseRef.current.abortController.abort();
    }
    dispatch(setConnectionState("reconnecting"));

    const outline = await dispatch(
      fetchProposalOutlineByOwner(ownerType, ownerId)
    );

    if (outline?.processingStatus === COMPLETED) {
      handleCompletedEvent();
      return;
    }
    if (outline?.processingStatus === FAILED) {
      handleFailedEvent(outline);
      return;
    }

    await runReconnectRoutine();
  };

  const connect = async (
    streamUrl,
    { isReconnect = false, epoch = sseRef.current.epoch } = {}
  ) => {
    if (!streamUrl || isStale(epoch)) {
      return;
    }
    if (sseRef.current.abortController) {
      sseRef.current.abortController.abort();
    }
    clearStallTimer();
    sseRef.current.abortController = new AbortController();

    // A fresh run (generate/regenerate) starts from a clean slate. A reconnect to an
    // EXISTING flow must NOT wipe the progress display or the seen-id / Last-Event-ID
    // dedup state: the backend replays already-delivered messages on reconnect, so
    // re-processing a stale gate-ready event (e.g. the type_review_ready the user
    // already answered) would snap the UI back to a gate it has passed. Preserving
    // seenIds dedups that replay; message ids are globally unique, so a preserved
    // seen-id never suppresses a genuinely new event.
    if (!isReconnect) {
      dispatch(setProgressError());
      dispatch(setProgressHistory());
      dispatch(resetGenerationProgress());
      sseRef.current.headers = {};
      sseRef.current.seenIds.clear();
      sseRef.current.reconnectAttempts = 0;
    }
    dispatch(setConnectionState("connected"));
    armStallTimer();

    const stream = fetchEventSource(streamUrl, {
      signal: sseRef.current.abortController.signal,
      openWhenHidden: true,
      headers: sseRef.current.headers,
      onmessage: onMessage,
      // fetch-event-source's default onclose (when unset) is a silent no-op when
      // the server ends the stream gracefully (e.g. the backend SseEmitter
      // completing/timing out) — no thrown error, so onerror never fires either.
      // Without this, reconnection depended entirely on the 90s stall timer as
      // the only line of defense; route a graceful close into the same recovery
      // path as onerror so an unexpected close is never silently ignored.
      onclose: () => {
        console.warn("SSE connection closed by server; checking for reconnect");
        onStall();
      },
      onerror: (error) => {
        console.error("SSE connection error:", error);
        // onStall() owns abort + connectionState + refetch/reconnect — no
        // need to duplicate that here.
        onStall();
        // Throw to stop fetch-event-source's own blind retry loop — the
        // stall/reconnect routine above now owns recovery.
        throw error;
      },
    });

    // onerror rethrows to stop fetch-event-source's own retry loop, which rejects the promise it
    // returned. Recovery is already owned by onStall, so this only stops the rejection going
    // unhandled.
    Promise.resolve(stream).catch((error) => {
      console.warn("SSE stream ended:", error?.message ?? error);
    });
  };

  // Best-effort JSON.parse for SSE gate payloads: a malformed/absent string must
  // degrade gracefully (null) rather than crash the stream.
  const safeJsonParse = (raw) => {
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error("Failed to parse SSE gate payload:", error);
      return null;
    }
  };

  // Shared by the type_review_ready / req_review_ready branches below: both carry
  // the same { ...counts/subset, block } shape (block itself a stringified
  // OutlineClarificationBlock, or null when the gate has no follow-up question).
  const handleGateReady = (gate, processingStatus, data) => {
    const payload = safeJsonParse(data.message) || {};
    const block = payload.block;
    const parsedBlock = safeJsonParse(block);

    dispatch(
      setGateState({
        gate,
        round: parsedBlock?.round ?? 0,
        block: block ?? null,
        answered: false,
      })
    );
    // Always make the clarification reflect THIS gate: set it when the gate has a
    // question block, otherwise clear it. Without the clear, a previous gate's
    // block (e.g. the type-review question) leaks into the next gate's view, which
    // reads `clarification` as its block source — so the type question would
    // reappear alongside the requirements at gate 2.
    if (parsedBlock) {
      dispatch(setClarification(parsedBlock));
    } else {
      dispatch(clearClarification());
    }
    // The structure proposal ({ tierLabel, groups }) is carried by the req_review_ready
    // payload (built from the extracted requirements); store it whenever present so the
    // RequirementsReviewGate editor is seeded.
    if (payload?.structureProposal) {
      dispatch(setStructureProposal(payload.structureProposal));
    }
    dispatch(setProcessingStatus(processingStatus));
    // The gate SSE payload only carries a document subset / verification counts,
    // not full requirement DTOs — hydrate the top-level requirements/outlineDocuments
    // the selectors read.
    dispatch(fetchProposalOutlineByOwner(ownerType, ownerId));
  };

  const resume = async (answers) => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    try {
      const outlineId = await dispatch(
        resumeOutline(ownerType, ownerId, answers)
      );
      await connect(buildStreamUrl(outlineId));
      dispatch(setProcessingStatus(IN_PROGRESS));
    } catch (error) {
      dispatch(setProgressError(getErrorMessage(error)));
    } finally {
      pendingRef.current = false;
    }
  };

  const onMessage = (evt) => {
    const { event: eventType, id } = evt;

    armStallTimer();
    dispatch(setConnectionState("connected"));
    sseRef.current.reconnectAttempts = 0;

    if (eventType === "heartbeat") {
      return;
    }

    // Acknowledged before the payload is read. Parsing first meant a malformed event threw out of
    // here without ever being recorded, so the reconnect resumed from the previous id and the
    // backend replayed the same broken event into the same throw, indefinitely.
    if (id) {
      sseRef.current.headers["Last-Event-ID"] = id;
    }

    if (id && sseRef.current.seenIds.has(id)) {
      return;
    }
    if (id) {
      sseRef.current.seenIds.add(id);
    }

    const data = safeJsonParse(evt.data || "{}");
    if (!data) {
      console.error("Discarding unparseable SSE payload", { eventType, id });
      return;
    }

    if (eventType === "failed") {
      clearStallTimer();
      handleFailedEvent(data);
      return;
    }

    if (eventType === "completed") {
      clearStallTimer();
      return handleCompletedEvent();
    }

    if (eventType === "clarification_required") {
      const block = safeJsonParse(data.message || "{}");
      if (!block) {
        console.error("Discarding unparseable clarification block", { id });
        return;
      }
      dispatch(setClarification(block));
      dispatch(setProcessingStatus(AWAITING_CLARIFICATION));
      dispatch(
        showModal(modalTypes.CLARIFICATION_REQUIRED, {
          ownerType,
          ownerId,
          onSubmit: resume,
        })
      );
      return;
    }

    if (eventType === "type_review_ready") {
      handleGateReady(GATE_TYPE_REVIEW, AWAITING_TYPE_REVIEW, data);
      // currentPhase drives which stepper step is "active" — advance it to the gate
      // phase so PhaseStepper's awaiting visual lands on confirmTypes. Uses the narrow
      // setCurrentPhase (not applyOutlineProgress) since the gate payload's `message` is
      // a stringified block, not a display message; see setCurrentPhase's comment.
      dispatch(setCurrentPhase(PHASE_BY_EVENT[eventType]));
      return;
    }

    if (eventType === "req_review_ready") {
      handleGateReady(GATE_REQ_REVIEW, AWAITING_REQ_REVIEW, data);
      dispatch(setCurrentPhase(PHASE_BY_EVENT[eventType]));
      return;
    }

    if (eventType === "document_progress") {
      dispatch(applyDocumentProgress(data));
      return;
    }

    if (eventType === "classification_detected") {
      dispatch(applyClassificationDetected(data));
      return;
    }

    if (!EVENT_TITLES[eventType]) {
      return;
    }

    dispatch(applyOutlineProgress({ eventType, data }));

    dispatch(
      addProgressHistory({
        payload: data,
        eventType,
        messageId: id,
        title: EVENT_TITLES[eventType],
      })
    );
  };

  // A 409 conflict means another request (another tab or user) already has a run
  // active or an outline present for this owner. Converge on the server's actual
  // state — checkOutlineStatus re-fetches the outline and reconnects to a live
  // run's SSE stream — instead of dead-ending this tab on an error message.
  const handleStartError = async (error) => {
    if (error?.response?.status === 409) {
      await checkOutlineStatus();
      return;
    }
    dispatch(setProgressError(getErrorMessage(error)));
    dispatch(showErrorModal(error));
  };

  const generate = async (_, documentIds) => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    try {
      if (checkBeforeAction) {
        const isAvailable = await checkBeforeAction();
        if (!isAvailable) {
          dispatch(
            setProgressError(
              "AI service is currently unavailable. Please try again later."
            )
          );
          return;
        }
      }
      dispatch(setDocumentIds(documentIds));
      // A brand new run starts with a clean slate — any answers accumulated
      // from a prior (now-abandoned) run no longer apply.
      dispatch(clearClarificationAnswers());
      const url = "/api/proposal-outlines";
      const rsp = await localhost.post(url, {
        ownerType,
        ownerId,
        documentIds,
      });

      await connect(buildStreamUrl(rsp?.data?.outlineId));

      dispatch(setProcessingStatus(IN_PROGRESS));
      // Seed the phase so the stepper reflects the current work immediately. The
      // staged pipeline's intermediate progress events fire server-side (right
      // after commit) before this client's SSE connects and are dropped, so the
      // stepper would otherwise sit blank / skip straight to the first gate. A
      // fresh run begins with classification → the "detecting" phase.
      dispatch(setCurrentPhase(PHASE_BY_EVENT.streaming_and_classifying));
    } catch (error) {
      await handleStartError(error);
    } finally {
      pendingRef.current = false;
    }
  };

  const reconnectInternal = async (epoch = sseRef.current.epoch) => {
    const url = `${getOwnerUrl(ownerType, ownerId)}/stream/reconnect`;
    const rsp = await localhost.get(url);
    if (isStale(epoch)) {
      return;
    }
    const streamUrl = rsp?.data?.streamUrl;
    // A missing streamUrl is a failed reconnect, not a silent success: connect()
    // early-returns on a falsy url, which would leave the "Reconnecting…" pill up
    // forever with the watchdog unarmed. Throw so the backoff loop counts the attempt.
    if (!streamUrl) {
      throw new Error("Reconnect response missing streamUrl");
    }
    await connect(streamUrl, { isReconnect: true, epoch });
  };

  // Public entry point for manual reconnect calls (checkOutlineStatus on
  // mount, the "Retry" button in ProposalOutline.js). Surfaces failures to
  // the UI. The watchdog's own backoff loop (reconnectAfterBackoff) calls
  // reconnectInternal directly instead, since it already catches failures
  // itself to drive its retry/give-up logic — going through this wrapper
  // would swallow the error before the backoff loop ever saw it.
  const reconnect = async (epoch = sseRef.current.epoch) => {
    try {
      await reconnectInternal(epoch);
    } catch (error) {
      if (isStale(epoch)) {
        return;
      }
      dispatch(setProgressError(getErrorMessage(error)));
    }
  };

  const regenerate = async (_, documentIds) => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    try {
      if (checkBeforeAction) {
        const isAvailable = await checkBeforeAction();
        if (!isAvailable) {
          dispatch(
            setProgressError(
              "AI service is currently unavailable. Please try again later."
            )
          );
          return;
        }
      }
      dispatch(setDocumentIds(documentIds));
      // Same fresh-run contract as generate: discard any prior clarification
      // answers before kicking off the new run.
      dispatch(clearClarificationAnswers());
      const url = `${getOwnerUrl(ownerType, ownerId)}/regenerate`;
      const rsp = await localhost.post(url, { documentIds });

      await connect(buildStreamUrl(rsp?.data?.outlineId));

      dispatch(setProcessingStatus(IN_PROGRESS));
      // See generate(): seed the classification ("detecting") phase so the stepper
      // reflects the work rather than sitting blank/skipping to the first gate.
      dispatch(setCurrentPhase(PHASE_BY_EVENT.streaming_and_classifying));
    } catch (error) {
      await handleStartError(error);
    } finally {
      pendingRef.current = false;
    }
  };

  // Resumes a FAILED run from the stage it died in, instead of re-paying the whole pipeline.
  // Like the gate confirms below, /retry answers 202 with no outlineId — every input the resumed
  // stage needs is already server-owned — so reconnectInternal's owner-scoped GET is what
  // re-attaches to the resumed stream.
  const retryFailedStage = async () => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    try {
      await dispatch(retryOutline(ownerType, ownerId));
      await reconnectInternal();
      dispatch(setProcessingStatus(IN_PROGRESS));
    } catch (error) {
      await handleStartError(error);
    } finally {
      pendingRef.current = false;
    }
  };

  // Gate confirm/re-extract mirror resume's guarded style: the backend confirm/re-extract
  // endpoints respond 202 with no body (unlike /resume, which echoes back an outlineId), so
  // there's no outlineId to hand buildStreamUrl — reconnectInternal's owner-scoped GET (already
  // used for mount/backoff reconnects) is the right tool to resume the SAME live stream.
  const confirmGateAndReconnect = async (gate, structure) => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    try {
      await dispatch(confirmGate(ownerType, ownerId, gate, structure));
      await reconnectInternal();
      dispatch(setProcessingStatus(IN_PROGRESS));
      // Advance the stepper to the stage this confirm kicks off (its progress
      // events fire before reconnect and are dropped): confirming types starts
      // extraction; confirming requirements starts outline generation.
      dispatch(
        setCurrentPhase(
          gate === GATE_TYPE_REVIEW
            ? PHASE_BY_EVENT.document_progress
            : PHASE_BY_EVENT.generating_outline
        )
      );
    } catch (error) {
      dispatch(setProgressError(getErrorMessage(error)));
    } finally {
      pendingRef.current = false;
    }
  };

  const confirmTypes = () => confirmGateAndReconnect(GATE_TYPE_REVIEW);

  const confirmRequirements = (structure) =>
    confirmGateAndReconnect(GATE_REQ_REVIEW, structure);

  // Thin wrappers: the caller (the gate wizard UI, Tasks 7-8) owns error handling and uses
  // the returned followUpBlock to render the next round — no reconnect here, the gate stays
  // parked until confirmed.
  const submitTypeAnswers = (answers) =>
    dispatch(submitGateAnswers(ownerType, ownerId, GATE_TYPE_REVIEW, answers));

  const submitReqAnswers = (answers) =>
    dispatch(submitGateAnswers(ownerType, ownerId, GATE_REQ_REVIEW, answers));

  // Requirements CRUD (Gate 2/Task 8): the underlying thunks (actions.js) already
  // own job-queue + try/catch + showErrorModal, so these are thin ownerType/ownerId-bound
  // wrappers — same shape as confirmRequirements/submitReqAnswers above.
  const addRequirement = (payload) =>
    dispatch(addRequirementAction(ownerType, ownerId, payload));

  const updateRequirement = (requirementId, payload) =>
    dispatch(
      updateRequirementAction(ownerType, ownerId, requirementId, payload)
    );

  const removeRequirement = (requirementId) =>
    dispatch(removeRequirementAction(ownerType, ownerId, requirementId));

  const reExtract = async (reclassify) => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    try {
      await dispatch(reExtractRequirements(ownerType, ownerId, reclassify));
      await reconnectInternal();
      dispatch(setProcessingStatus(IN_PROGRESS));
      // Reclassify sends the run back through classification (detecting); a plain
      // re-extract resumes at extraction. Seed the matching phase so the stepper
      // reflects the restarted work immediately (see generate() for the rationale).
      dispatch(
        setCurrentPhase(
          reclassify
            ? PHASE_BY_EVENT.streaming_and_classifying
            : PHASE_BY_EVENT.document_progress
        )
      );
    } catch (error) {
      dispatch(setProgressError(getErrorMessage(error)));
    } finally {
      pendingRef.current = false;
    }
  };

  const checkOutlineStatus = async (epoch = sseRef.current.epoch) => {
    const outline = await dispatch(
      fetchProposalOutlineByOwner(ownerType, ownerId)
    );

    if (isStale(epoch)) {
      return;
    }

    if (!outline) {
      setIsInitialized(true);
    } else if (RECONNECTABLE_STATUSES.has(outline.processingStatus)) {
      // Reconnect for a live run, a staged in-progress phase (CLASSIFYING/EXTRACTING/GENERATING),
      // or a paused (awaiting-clarification/gate) one: the SSE replay re-delivers the
      // clarification_required/type_review_ready/req_review_ready event, which re-opens the
      // modal/gate on page load.
      // PENDING (accepted, worker not yet started) also has a live flow — its stream delivers
      // events once the worker claims the row.
      await reconnect();
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    // Re-armed on every owner change: the cleanup below sets this on the way out, and the ref
    // survives the re-run, so leaving it set would kill reconnects for the owner we just moved to.
    sseRef.current.unmounted = false;
    const epoch = sseRef.current.epoch + 1;
    sseRef.current.epoch = epoch;

    if (ownerType && ownerId != null) {
      checkOutlineStatus(epoch);

      if (entityType) {
        dispatch(getDocuments(ownerId, entityType));
        dispatch(getDocTypes(entityType));
      }
    }

    return () => {
      sseRef.current.unmounted = true;
      clearStallTimer();
      clearBackoffTimer();
      if (sseRef.current.abortController) {
        sseRef.current.abortController.abort();
      }
      sseRef.current.headers = {};
      sseRef.current.seenIds.clear();
      // ModalRoot is mounted at the app root, outside the router, so a clarification prompt raised
      // by this stream otherwise stays on screen across navigation — and submitting it resumes the
      // owner the user has already left.
      dispatch(hideModal(modalTypes.CLARIFICATION_REQUIRED));
    };
  }, [ownerType, ownerId]);

  return {
    isInitialized,
    generate,
    regenerate,
    resume,
    retryFailedStage,
    reconnect,
    confirmTypes,
    confirmRequirements,
    submitTypeAnswers,
    submitReqAnswers,
    reExtract,
    addRequirement,
    updateRequirement,
    removeRequirement,
  };
};
